<template>
  <b-row>
    <b-col v-if="!loading" cols="12" lg="12">
      <b-row class="mb-3">
        <b-col cols="12">
          <div class="complaint-top-actions">
            <b-button
              variant="secondary"
              size="sm"
              class="complaint-back-button"
              @click="goBack"
            >
              <i class="fas fa-arrow-left"></i> Voltar
            </b-button>
            <b-button
              v-if="isCompanyRoot"
              variant="primary"
              size="sm"
              class="complaint-print-button"
              @click="openPrintModal"
            >
              <i class="fas fa-print" aria-hidden="true"></i> Imprimir
            </b-button>
          </div>
        </b-col>
      </b-row>
      <mentions-panel
        :mentions="mentions"
        :current-mention.sync="currentMention"
        @confirm="confirmMention"
        @discard="discardMention"
      />
      <b-row>
        <b-col md="9">
          <info-card
            :complaint="complaint"
            :previous-protocol-id="previousProtocolId"
            :previous-protocol-type="previousProtocolType"
            :current-mention="currentMention"
            :classification-maps="classificationMaps"
            :master-user="masterUser"
            :is-complaint-admin="isComplaintAdmin"
            @download-file="downloadFile"
          />
        </b-col>
        <b-col md="3">
          <committee-card
            :committee="complaint.committee"
            :can-edit="canEdit"
            :is-complaint-admin="isComplaintAdmin"
            :master-user="masterUser"
            :get-committee-user="getCommitteeUser"
            @open-committee="openCommitteeModal"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col md="9">
          <processing-card
            :loading-events="loadingEvents"
            :events="events"
            :can-edit="canEdit"
            :can-reopen="canReopen"
            :can-hide-irrelevant="canHideIrrelevant"
            :classification-maps="classificationMaps"
            :company-users="companyUsers"
            :managed-by-company-users="managedByCompanyUsers"
            :get-committee-user="getCommitteeUser"
            :complaint="complaint"
            @add-event="$refs.eventModal.show()"
            @resolve-pendency="resolvePendency"
            @resolve-task="resolveTask"
            @resend-task="resendTask"
            @verify-course-status="verifyCourseStatus"
            @toggle-irrelevant="openIrrelevantModal"
          />
        </b-col>
        <b-col md="3">
          <phone-card
            :complaint="complaint"
            :can-edit="canEdit"
            :conformita-user="conformitaUser"
            :phone-service-protocol.sync="phoneServiceProtocol"
            :phone-service-receive-date.sync="phoneServiceReceiveDate"
            :phone-service-receive-time.sync="phoneServiceReceiveTime"
            :old-phone-service-protocol="oldPhoneServiceProtocol"
            @associate="onAssociatePhoneServiceProtocol"
            @cancel-replace="cancelPhoneServiceProtocolReplace"
            @replace="replacePhoneServiceProtocol"
          />
          <denounced-list
            :denounceds="denounceds"
            :can-edit="canEdit"
            :loading-denounceds="loadingDenounceds"
            :is-complaint-admin="isComplaintAdmin"
            :master-user="masterUser"
            :is-committee-member="isCommitteeMember"
            :permissions="permissions"
            @open="$refs.denouncedModal.show()"
            @refresh="onDenouncedRefresh"
            @loading="loadingDenounceds = $event"
          />
          <classification-card
            :complaint="complaint"
            :can-edit="canEdit"
            @open="$refs.classificationModal.show()"
          />
          <monitoring-card
            :complaint="complaint"
            :can-edit="canEdit"
            :loading-monitorings="loadingMonitorings"
            :monitorings="monitorings"
            :company-users="companyUsers"
            :managed-by-company-users="managedByCompanyUsers"
            :conformita-users="conformitaUsers"
            @open="$refs.monitoringModal.show()"
            @toggle-response="toggleMonitoringResponse(complaint)"
            @toggle-visibility="toggleMonitoringVisibility"
          />
        </b-col>
      </b-row>
      <b-modal
        ref="printModal"
        title="Imprimir denúncia"
        size="md"
        ok-title="Imprimir"
        cancel-title="Cancelar"
        :ok-disabled="printingComplaint"
        @ok="onPrintModalOk"
      >
        <b-form-group label="Selecione os parâmetros que devem aparecer:">
          <b-form-checkbox-group
            v-model="selectedPrintSections"
            :options="printSectionOptions"
            stacked
          />
        </b-form-group>
      </b-modal>

      <div
        id="complaint-print-content"
        class="complaint-print-content"
        v-if="complaint && complaint._id"
      >
        <b-row>
          <b-col>
            <h4>
              Denúncia {{ complaint.protocol }}
              <small v-if="complaint.previousProtocol">
                (Procotolo anterior: {{ complaint.previousProtocol }})</small
              >
              <span class="float-right"
                >Status: {{ getStatusText(complaint.status) }}</span
              >
            </h4>
            <p v-if="complaint.phoneServiceProtocol">
              <strong>Protocolo Telefônico: </strong
              >{{ complaint.phoneServiceProtocol }}
            </p>
            <strong>Recebida: </strong>{{ complaint.createdAt | datetime }}
            <span class="float-right">
              <strong>Última Movimentação: </strong
              >{{ complaint.updatedAt | datetime }}
            </span>
          </b-col>
        </b-row>
        <b-row v-if="hasPrintSection('information')">
          <b-col md="12">
            <template
              v-if="
                masterUser ||
                !(complaint.committee && complaint.committee.hideDenouncerData)
              "
            >
              <hr class="hr-text" />
              <h5 v-if="complaint.anonymous">Denúncia Anônima</h5>
              <h5 v-else>
                Denunciante
                <small
                  v-if="complaint.committee.hideDenouncerData && masterUser"
                  >(Os dados do denunciante estão ocultados para os membros do
                  comitê)</small
                >
              </h5>
              <template
                v-if="
                  !complaint.anonymous && !complaint.committee.hideDenouncerData
                "
              >
                <b-row>
                  <b-col cols="4">
                    <label>Nome:</label>
                    {{ complaint.denouncer.fullName | noInformation }}
                  </b-col>
                  <b-col cols="4">
                    <label>CPF:</label>
                    {{ complaint.denouncer.cpf | noInformation }}
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="4">
                    <label>Telefone:</label>
                    {{ complaint.denouncer.phone | noInformation }}
                  </b-col>
                  <b-col cols="4">
                    <label>Email:</label>
                    {{ complaint.denouncer.email | noInformation }}
                  </b-col>
                </b-row>
              </template>
            </template>
            <hr class="hr-text" />
            <b-row class="pb-3">
              <b-col md="12">
                <h5>Descrição</h5>
                <div>{{ complaint.text }}</div>
                <template v-if="complaint.submittedClassification">
                  <hr class="hr-text" data-content="Indicadores informados" />
                  <template v-for="opt in Object.values(ClassificationTypeMap)">
                    <div
                      v-if="
                        complaint.submittedClassification[opt] &&
                        ClassificationName[opt]
                      "
                      :key="opt"
                    >
                      <b>{{ ClassificationName[opt] }}:</b>
                      {{
                        (classificationMaps[opt] || {})[
                          complaint.submittedClassification[opt]
                        ]
                      }}<br />
                    </div>
                  </template>
                  <hr class="hr-text" />
                </template>
              </b-col>
            </b-row>
            <b-row class="pb-3">
              <b-col md="12">
                <h5>Data dos fatos</h5>
                <div>{{ complaint.date | noInformation }}</div>
              </b-col>
            </b-row>
            <b-row class="pb-3">
              <b-col md="12">
                <h5>Local onde ocorreram os fatos</h5>
                <div>{{ complaint.location | noInformation }}</div>
              </b-col>
            </b-row>
            <b-row class="pb-3">
              <b-col md="12">
                <h5>Como teve conhecimento dos fatos</h5>
                <div>{{ complaint.knowAbout | noInformation }}</div>
              </b-col>
            </b-row>
            <b-row class="pb-3">
              <b-col md="12">
                <h5>Testemunhas ou pessoas que tiverem conhecimento</h5>
                <div>{{ complaint.witnesses | noInformation }}</div>
              </b-col>
            </b-row>
            <b-row class="pb-3">
              <b-col md="12">
                <h5>Danos financeiros</h5>
                <div>{{ complaint.witnesses | noInformation }}</div>
              </b-col>
            </b-row>
            <b-row v-if="complaint.files.length > 0">
              <b-col md="12">
                <h5>Anexos</h5>
                <b-list-group>
                  <b-list-group-item
                    v-for="(file, idx) in complaint.files"
                    :key="idx"
                    class="d-flex justify-content-between align-items-center"
                  >
                    {{ file.name }}
                  </b-list-group-item>
                </b-list-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row v-if="hasPrintSection('classification')">
          <b-col>
            <hr class="hr-text" />
            <h4>Classificação</h4>
            <template v-if="complaint.classification">
              <b-row>
                <b-col v-if="complaint.classification.receivingMeans" md="4">
                  <strong>Meio de Recebimento: </strong
                  >{{
                    getClassificationLabel(
                      "receivingMeans",
                      complaint.classification.receivingMeans
                    )
                  }}
                </b-col>
                <b-col v-if="complaint.classification.channelConfidence" md="4">
                  <strong>Confiança no Canal: </strong
                  >{{
                    getClassificationLabel(
                      "channelConfidence",
                      complaint.classification.channelConfidence
                    )
                  }}
                </b-col>
                <b-col v-if="complaint.classification.origin" md="4">
                  <strong>Origem da Comunicação: </strong
                  >{{
                    getClassificationLabel(
                      "origin",
                      complaint.classification.origin
                    )
                  }}
                </b-col>
                <b-col v-if="complaint.classification.reportingAgent" md="4">
                  <strong>Agente Denunciante: </strong
                  >{{
                    getClassificationLabel(
                      "reportingAgent",
                      complaint.classification.reportingAgent
                    )
                  }}
                </b-col>
                <b-col v-if="complaint.classification.level" md="4">
                  <strong>Classificação/Classe Interna: </strong
                  >{{
                    getClassificationLabel(
                      "level",
                      complaint.classification.level
                    )
                  }}
                </b-col>
                <b-col v-if="complaint.classification.nature" md="4">
                  <strong>Natureza: </strong
                  >{{
                    getClassificationLabel(
                      "nature",
                      complaint.classification.nature
                    )
                  }}
                </b-col>
                <b-col
                  v-if="complaint.classification.responsibleDepartment"
                  md="4"
                >
                  <strong>Área responsável pelo tratamento: </strong
                  >{{
                    getClassificationLabel(
                      "responsibleDepartment",
                      complaint.classification.responsibleDepartment
                    )
                  }}
                </b-col>
                <b-col
                  v-if="complaint.classification.involvedDepartment"
                  md="4"
                >
                  <strong>Setor da Ocorrência: </strong
                  >{{
                    getClassificationLabel(
                      "involvedDepartment",
                      complaint.classification.involvedDepartment
                    )
                  }}
                </b-col>
                <b-col v-if="complaint.classification.businessGroup" md="4">
                  <strong>Empresa do Grupo: </strong
                  >{{
                    getClassificationLabel(
                      "businessGroup",
                      complaint.classification.businessGroup
                    )
                  }}
                </b-col>
                <b-col v-if="complaint.classification.businessUnit" md="4">
                  <strong>Unidade/Posto/Filial: </strong
                  >{{
                    getClassificationLabel(
                      "businessUnit",
                      complaint.classification.businessUnit
                    )
                  }}
                </b-col>
                <b-col v-if="complaint.classification.relatedProject" md="4">
                  <strong>Contrato/Projeto: </strong
                  >{{
                    getClassificationLabel(
                      "relatedProject",
                      complaint.classification.relatedProject
                    )
                  }}
                </b-col>
              </b-row>
            </template>
          </b-col>
        </b-row>
        <b-row v-if="hasPrintSection('committee')">
          <b-col cols="12">
            <hr class="hr-text" />
            <h4>Comissão de Apuração</h4>
            <template v-if="complaint.committee">
              <template v-if="complaint.committee.admin">
                <strong>Relator: </strong
                >{{ getCommitteeUser(complaint.committee.admin).name }}
                {{ getCommitteeUser(complaint.committee.admin).surname }}
                <em
                  v-if="!getCommitteeUser(complaint.committee.admin).active"
                  class="text-warning"
                >
                  Inativo</em
                >
                <br />
              </template>
              <template v-if="complaint.committee.users">
                <strong>Membros:</strong>
                <ul>
                  <li
                    v-for="(user, idx) in complaint.committee.users"
                    :key="idx"
                  >
                    {{ getCommitteeUser(user).name }}
                    {{ getCommitteeUser(user).surname }}
                    <em
                      v-if="!getCommitteeUser(user).active"
                      class="text-warning"
                    >
                      Inativo</em
                    >
                  </li>
                </ul>
              </template>
            </template>
          </b-col>
        </b-row>
        <b-row v-if="hasPrintSection('processing')">
          <b-col cols="12">
            <hr class="hr-text" />
            <h4>Processamento</h4>
            <b-card v-for="event in events" :key="event._id" no-header>
              <div>#{{ event.number }}</div>
              <label><strong>Tipo:</strong></label>
              {{ labelEventType(event.type) }}
              <br />
              <label
                v-if="event.type === EventType.STATUS_CHANGE"
                class="card-text"
              >
                <strong>Novo Status: </strong> {{ getStatusText(event.status) }}
              </label>
              <p class="card-text">
                <label><strong>Descrição:</strong></label>
                {{ event.description }}
              </p>
              <template v-if="event.type === EventType.WITH_ATTACHMENT">
                <b-list-group v-if="event.files && event.files.length > 0">
                  <b-list-group-item
                    v-for="(file, idx) in event.files"
                    :key="idx"
                    class="d-flex justify-content-between align-items-center"
                  >
                    {{ file.name }}
                  </b-list-group-item>
                </b-list-group>
                <b-alert v-else show variant="warning"
                  >Arquivo não recebido!</b-alert
                >
              </template>
              <p class="card-text">
                <small class="text-muted">
                  <i class="fa fa-calendar-alt" aria-hidden="true" />
                  {{ event.createdAt | datetime }} -
                  <template v-if="event.task && event.task.email">
                    <i
                      class="fa fa-user-friends text-warning"
                      aria-hidden="true"
                    ></i>
                    {{ event.task.email }}
                  </template>
                  <template v-else-if="companyUsers[event.user]">
                    <i class="fa fa-user" aria-hidden="true" />
                    {{ companyUsers[event.user].name }}
                    {{ companyUsers[event.user].surname }}
                  </template>
                  <template v-else-if="conformitaUsers[event.user]">
                    <i class="fa fa-user-tie text-info" aria-hidden="true" />
                    {{ conformitaUsers[event.user].name }}
                    {{ conformitaUsers[event.user].surname }}
                  </template>
                </small>
              </p>
            </b-card>
          </b-col>
        </b-row>
        <b-row v-if="hasPrintSection('denounced')">
          <b-col cols="12">
            <hr class="hr-text" />
            <h4>Investigados</h4>
            <ul>
              <li v-for="denounced in denounceds" :key="denounced._id">
                <strong>{{ denounced.name }}</strong>
                <br />
                <small class="text-muted">
                  {{ denounced.cpfcnpj || "Não informado" }}
                  {{ denounced.office ? " - " + denounced.office : "" }}
                  {{
                    denounced.department
                      ? " (" + denounced.department + ")"
                      : ""
                  }}
                </small>
              </li>
            </ul>
          </b-col>
        </b-row>
        <b-row v-if="hasPrintSection('external')">
          <b-col cols="12">
            <hr class="hr-text" />
            <h4>Acompanhamento Externo</h4>
            <b-card v-for="(monitoring, idx) in monitorings" :key="idx">
              <p class="card-text">
                <i class="fa fa-calendar-alt" aria-hidden="true" />
                {{ monitoring.createdAt | datetime }}<br />
                <template v-if="monitoring.type !== MonitoringType.RESPONSE">
                  <template v-if="companyUsers[monitoring.user]">
                    <i class="fa fa-user" aria-hidden="true" />
                    {{ companyUsers[monitoring.user].name }}
                    {{ companyUsers[monitoring.user].surname }}
                  </template>
                  <template v-else-if="conformitaUsers[monitoring.user]">
                    <i class="fa fa-user-tie text-info" aria-hidden="true" />
                    {{ conformitaUsers[monitoring.user].name }}
                    {{ conformitaUsers[monitoring.user].surname }}
                  </template>
                </template>
              </p>
              <p
                v-if="monitoring.type === MonitoringType.COMPANY"
                class="card-text"
              >
                {{ monitoring.description }}
              </p>
              <p
                v-else-if="monitoring.type === MonitoringType.RESPONSE"
                class="card-text text-info"
              >
                <i class="fas fa-reply" aria-hidden="true" />
                {{ monitoring.response && monitoring.response.text }}
              </p>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <event-modal
        ref="eventModal"
        :complaint="complaint"
        :pendency="pendencyEvent"
        :task="taskEvent"
        @new-event="loadEvents"
        @status-changed="onStatusChanged"
        @cancel="pendencyEvent = null"
      ></event-modal>
      <committee-modal
        ref="committeeModal"
        :complaint="complaint"
        @changed="onCommitteeChanged"
      ></committee-modal>
      <monitoring-modal
        ref="monitoringModal"
        :complaint="complaint"
        @new-monitoring="loadMonitoring"
      ></monitoring-modal>
      <classification-modal
        ref="classificationModal"
        :complaint="complaint"
        @classification-changed="onClassificationChanged"
      ></classification-modal>
      <denounced-modal
        v-if="
          isComplaintAdmin ||
          masterUser ||
          isCommitteeMember ||
          (permissions.complaint && permissions.complaint.readall)
        "
        ref="denouncedModal"
        :complaint="complaint"
        @new-denounced="loadDenounceds"
      ></denounced-modal>
      <b-modal
        id="irrelevant-event-modal"
        ref="irrelevantModalRef"
        title="Marcar Movimentação como Irrelevante"
        :ok-disabled="
          irrelevantModal.submitting || !irrelevantModal.justification.trim()
        "
        ok-title="Confirmar"
        ok-variant="danger"
        cancel-title="Cancelar"
        @hide="onHideIrrelevantModal"
        @ok="confirmIrrelevant"
      >
        <div v-if="irrelevantModal.event">
          <b-alert show variant="warning" class="py-2">
            <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
            <strong>Esta ação não pode ser revertida.</strong>
            A movimentação ficará oculta para usuários sem permissão e o
            conteúdo não poderá ser exibido novamente.
          </b-alert>
          <p class="mb-2">
            Confirma marcar a movimentação
            <strong>#{{ irrelevantModal.event.number }}</strong> como
            irrelevante?
          </p>
          <b-form-group
            label="Justificativa"
            description="Descreva o motivo (obrigatório)."
            :state="irrelevantJustificationState"
            invalid-feedback="Informe a justificativa"
          >
            <b-form-textarea
              v-model="irrelevantModal.justification"
              :disabled="irrelevantModal.submitting"
              :state="irrelevantJustificationState"
              rows="3"
              max-rows="6"
              placeholder="Digite a justificativa"
            />
          </b-form-group>
          <b-alert
            v-if="irrelevantModal.errorMessage"
            variant="danger"
            show
            class="py-1"
          >
            {{ irrelevantModal.errorMessage }}
          </b-alert>
        </div>
      </b-modal>
      <b-modal
        v-if="
          complaint.committee &&
          complaint.committee.hideDenouncerData &&
          (masterUser || isComplaintAdmin)
        "
        id="denouncerData"
        title="Denunciante"
        size="md"
        ok-only
      >
        <b-row>
          <b-col cols="6">
            <label>Nome:</label>
            {{ complaint.denouncer.fullName | noInformation }}
          </b-col>
          <b-col cols="6">
            <label>CPF:</label> {{ complaint.denouncer.cpf | noInformation }}
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <label>Telefone:</label>
            {{ complaint.denouncer.phone | noInformation }}
          </b-col>
          <b-col cols="6">
            <label>Email:</label>
            {{ complaint.denouncer.email | noInformation }}
          </b-col>
        </b-row>
      </b-modal>
    </b-col>
  </b-row>
</template>

<script>
import {
  getComplaint,
  getEvents,
  associatePhoneServiceProtocol,
  getMonitoring,
  getDenounceds,
  setMonitoringVisibility,
  setMonitoringResponseEnabled,
  findComplaintIdByProtocol,
  resendTask,
  verifyCourseStatus,
  setEventIrrelevant,
} from "@/api/complaint";
import { getSystemUsers } from "@/api/user";
import { getRecordMentions, updateMention } from "@/api/mentions";
import { getCompanyUsers } from "@/api/company";
import { getBadgeColor, getStatusText, labelEventType } from "../utils";
import { mapGetters } from "vuex";
import EventType from "../../../../../backend/models/EventType.ts";
import ComplaintStatus from "../../../../../backend/models/ComplaintStatus.ts";
import MonitoringType from "../../../../../backend/models/MonitoringType.ts";
import {
  ClassificationTypeMap,
  ClassificationName,
} from "../../../../../backend/models/ClassificationType.ts";

import { mask } from "vue-the-mask";
import EventModal from "../event-modal";
import CommitteeModal from "../committee-modal";
import MonitoringModal from "../monitoring-modal";
import ClassificationModal from "../classification-modal";
import DenouncedModal from "../denounced-modal";
import typologyValues from "../../../../../staticData/typology.json";
import denouncedRelationValues from "../../../../../staticData/denouncedRelation.json";
import moment from "moment/moment";

export default {
  name: "ComplaintView",
  components: {
    MentionsPanel: () =>
      import(
        /* webpackChunkName: "complaint-view" */
        "./MentionsPanel.vue"
      ),
    InfoCard: () =>
      import(
        /* webpackChunkName: "complaint-view" */
        "./InfoCard.vue"
      ),
    CommitteeCard: () =>
      import(
        /* webpackChunkName: "complaint-view" */
        "./CommitteeCard.vue"
      ),
    ProcessingCard: () =>
      import(
        /* webpackChunkName: "complaint-view" */
        "./ProcessingCard.vue"
      ),
    PhoneCard: () =>
      import(
        /* webpackChunkName: "complaint-view" */
        "./PhoneCard.vue"
      ),
    DenouncedList: () =>
      import(
        /* webpackChunkName: "complaint-view" */
        "./DenouncedList.vue"
      ),
    ClassificationCard: () =>
      import(
        /* webpackChunkName: "complaint-view" */
        "./ClassificationCard.vue"
      ),
    MonitoringCard: () =>
      import(
        /* webpackChunkName: "complaint-view" */
        "./MonitoringCard.vue"
      ),
    DenouncedModal,
    EventModal,
    CommitteeModal,
    MonitoringModal,
    ClassificationModal,
  },
  directives: { mask },
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      EventType,
      MonitoringType,
      ComplaintStatus,
      ClassificationTypeMap,
      ClassificationName,
      typologyValues,
      denouncedRelationValues,
      printingComplaint: false,
      printSectionOptions: [
        { value: "information", text: "Informações" },
        { value: "processing", text: "Processamento" },
        { value: "committee", text: "Comitê" },
        { value: "denounced", text: "Investigados" },
        { value: "classification", text: "Classificação" },
        { value: "external", text: "Acompanhamento Externo" },
      ],
      selectedPrintSections: [
        "information",
        "processing",
        "committee",
        "denounced",
        "classification",
        "external",
      ],
      mentions: [],
      currentMention: null,
      loading: false,
      fullComplaintView: false,
      complaint: {},
      companyUsers: {},
      managedByCompanyUsers: {},
      conformitaUsers: {},
      loadingEvents: false,
      events: [],
      phoneServiceProtocol: null,
      phoneServiceReceiveDate: null,
      phoneServiceReceiveTime: null,
      oldPhoneServiceProtocol: null,
      oldPhoneServiceReceiveDate: null,
      oldPhoneServiceReceiveTime: null,
      loadingMonitorings: false,
      monitorings: [],
      loadingDenounceds: false,
      pendencyEvent: null,
      taskEvent: null,
      denounceds: [],
      previousProtocolId: null,
      previousProtocolType: "complaint",
      irrelevantModal: {
        event: null,
        justification: "",
        submitting: false,
        errorMessage: "",
      },
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions", "classificationMaps"]),
    isCompanyRoot() {
      return this.profile && this.profile.role === "company-root";
    },
    masterUser() {
      const role = this.profile.role;
      if (this.complaint && this.complaint.designatedAdmin) {
        return true;
      }
      return role === "root" || role === "company-root";
    },
    conformitaUser() {
      return !!this.conformitaUsers[this.profile._id];
    },
    isComplaintAdmin() {
      if (!this.complaint || !this.complaint.committee) return false;
      return this.profile._id == this.complaint.committee.admin;
    },
    isCommitteeMember() {
      if (!this.complaint || !this.complaint.committee) return false;
      const users = this.complaint.committee.users || [];
      return users.some((userId) => userId == this.profile._id);
    },
    isCompanyManagedByOther() {
      return !!(
        this.complaint &&
        this.complaint.company &&
        this.complaint.company.managedBy
      );
    },
    canEdit() {
      if (parseInt(this.profile.role) === this.profile.role) {
        return (
          (this.permissions.complaint && this.permissions.complaint.edit) ||
          this.permissions.complaint.create
        );
      }
      return true;
    },
    canReopen() {
      if (this.complaint.status !== ComplaintStatus.CONCLUDED) {
        return false;
      }
      if (parseInt(this.profile.role) === this.profile.role) {
        return (
          (this.permissions.complaint && this.permissions.complaint.edit) ||
          this.permissions.complaint.create
        );
      }
      return true;
    },
    hasAlternateComplaintForm() {
      return this.complaint.company?.modules?.complaint?.alternateForm;
    },
    irrelevantJustificationState() {
      if (this.irrelevantModal.justification === "") return null;
      return this.irrelevantModal.justification.trim().length > 0;
    },
    canHideIrrelevant() {
      const role = this.profile && this.profile.role;
      if (role === "root") return true;
      if (role === "company-root") {
        console.log(this.profile.role, this.profile.managedCompanies);
        const managed = this.profile.managedCompanies || [];
        return managed.length > 0;
      }
      return false;
    },
  },
  async created() {
    this.loading = true;
    try {
      this.mentions = (await getRecordMentions("complaint", this.id)).data;
      this.mentions.forEach((mention, index) => {
        this.$set(this.mentions[index], "verified", !!mention.verified);
      });
    } catch (error) {
      if (error.response?.status !== 403) {
        throw error;
      }
    }
    const response = await getComplaint(this.id);
    const complaint = response.data;
    this.complaint = complaint;

    let resp = await getCompanyUsers(complaint.company._id, true);
    this.companyUsers = resp.data.reduce((obj, user) => {
      obj[user["_id"]] = user;
      return obj;
    }, {});

    if (complaint.company.managedBy) {
      let managedCompanyUsers = (
        await getCompanyUsers(complaint.company.managedBy, true)
      ).data;
      this.managedByCompanyUsers = managedCompanyUsers.reduce((obj, user) => {
        obj[user["_id"]] = user;
        return obj;
      }, {});
    }

    resp = await getSystemUsers();
    this.conformitaUsers = resp.data.reduce((obj, user) => {
      obj[user["_id"]] = user;
      return obj;
    }, {});

    this.loadMonitoring();
    this.loadClassifications();
    this.loadDenounceds();
    await this.loadEvents();
    await this.loadPreviousProtocolInfo();
    const { event } = this.$route.query;
    if (event) {
      let query = Object.assign({}, this.$route.query);
      delete query.event;
      this.$router.replace({ query });

      let attempts = 0;
      const maxAttempts = 50; // Limite de tentativas (aproximadamente 2.5 segundos)

      const showQueryEvent = () => {
        if (event) {
          const element = document.getElementById(`event-${event}`);
          if (!element) {
            attempts++;
            if (attempts < maxAttempts) {
              this.$nextTick(showQueryEvent);
            } else {
              console.warn(
                `Elemento event-${event} não encontrado após ${maxAttempts} tentativas`
              );
            }
            return; // Sair da função se o elemento não existe
          }
          this.showEvent(event);
        }
      };
      this.$nextTick(showQueryEvent);
    }

    this.loading = false;
  },
  methods: {
    moment,
    getStatusText,
    getBadgeColor,
    labelEventType,
    openPrintModal() {
      this.$refs.printModal.show();
    },
    hasPrintSection(section) {
      return this.selectedPrintSections.includes(section);
    },
    getClassificationLabel(type, value) {
      if (!value) return "";
      return (this.classificationMaps[type] || {})[value] || value;
    },
    onPrintModalOk(event) {
      event.preventDefault();
      if (!this.selectedPrintSections.length) {
        this.$bvToast.toast(
          "Selecione pelo menos um parâmetro para imprimir.",
          {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
        return;
      }
      this.printComplaint();
    },
    printComplaint() {
      this.printingComplaint = true;
      this.$refs.printModal.hide();
      this.$nextTick(() => {
        this.$htmlToPaper("complaint-print-content", () => {
          this.printingComplaint = false;
        });
      });
    },
    getEventUserLabel(event) {
      if (event.task && event.task.email) {
        return event.task.email;
      }
      if (this.companyUsers[event.user]) {
        return `${this.companyUsers[event.user].name} ${
          this.companyUsers[event.user].surname
        }`;
      }
      if (this.conformitaUsers[event.user]) {
        return `${this.conformitaUsers[event.user].name} ${
          this.conformitaUsers[event.user].surname
        }`;
      }
      return "Usuário removido";
    },
    getMonitoringUserLabel(userId) {
      if (this.companyUsers[userId]) {
        return `${this.companyUsers[userId].name} ${this.companyUsers[userId].surname}`;
      }
      if (this.conformitaUsers[userId]) {
        return `${this.conformitaUsers[userId].name} ${this.conformitaUsers[userId].surname}`;
      }
      return "Usuário removido";
    },
    getCommitteeUser(userId) {
      if (!userId) {
        return {
          _id: null,
          name: "Usuário removido",
          surname: "",
          active: false,
        };
      }
      const user =
        this.companyUsers[userId] ||
        (this.isCompanyManagedByOther
          ? this.managedByCompanyUsers[userId]
          : null);
      if (user) return user;
      return {
        _id: userId,
        name: "Usuário removido",
        surname: "",
        active: false,
      };
    },
    async confirmMention(mention) {
      try {
        this.$set(mention, "verified", true);
        this.$set(mention, "blocked", true);
        await updateMention(mention, {
          blocked: true,
          verified: true,
        });
        this.$bvToast.toast("Menção confirmada com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        // Reverter as mudanças em caso de erro
        this.$set(mention, "verified", false);
        this.$set(mention, "blocked", mention.blocked);
        this.$bvToast.toast("Erro ao confirmar menção. Tente novamente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        console.error("Erro ao confirmar menção:", error);
      }
    },
    async discardMention(mention) {
      try {
        this.$set(mention, "verified", true);
        this.$set(mention, "blocked", false);
        await updateMention(mention, {
          blocked: false,
          verified: true,
        });
        this.$bvToast.toast("Menção descartada com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        // Reverter as mudanças em caso de erro
        this.$set(mention, "verified", false);
        this.$set(mention, "blocked", mention.blocked);
        this.$bvToast.toast("Erro ao descartar menção. Tente novamente.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        console.error("Erro ao descartar menção:", error);
      }
    },
    showEvent(id) {
      const element = document.getElementById(`event-${id}`);
      const offset = 55;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    },
    getEventNumber(id) {
      return this.events.find((e) => e._id === id).number;
    },
    goBack() {
      this.$router.go(-1);
    },
    resolvePendency(event) {
      this.pendencyEvent = event;
      this.$refs.eventModal.show();
    },
    resolveTask(event) {
      this.taskEvent = event;
      this.$refs.eventModal.show();
    },
    async resendTask(event) {
      try {
        await resendTask(this.complaint._id, event._id);
        this.$bvToast.toast("E-mail reenviado com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        if (error.response.status === 403 || error.response.status === 404) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },
    async verifyCourseStatus(event, showNotification = true) {
      try {
        const response = await verifyCourseStatus(
          this.complaint._id,
          event._id
        );

        // Update the event in the local events array
        const eventIndex = this.events.findIndex((e) => e._id === event._id);
        if (eventIndex !== -1 && this.events[eventIndex].courseAssignment) {
          this.events[eventIndex].courseAssignment.status =
            response.data.status;
          if (response.data.certificateNumber) {
            this.events[eventIndex].courseAssignment.certificateNumber =
              response.data.certificateNumber;
          }
        }

        if (showNotification) {
          this.$bvToast.toast(
            `Status atualizado: ${this.getCourseStatusText(
              response.data.status
            )}`,
            {
              title: "Sucesso",
              toastClass: "custom-toast custom-toast-success",
              variant: "light",
              solid: true,
              autoHideDelay: 5000,
              noCloseButton: false,
            }
          );
        }
      } catch (error) {
        if (showNotification) {
          this.$bvToast.toast(
            error.response?.data || "Erro ao verificar status do curso",
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
        }
      }
    },
    getCourseStatusText(status) {
      const texts = {
        pending: "Pendente",
        in_progress: "Em Progresso",
        completed: "Concluído",
        cancelled: "Cancelado",
      };
      return texts[status] || status;
    },
    async loadEvents() {
      this.pendencyEvent = null;
      this.taskEvent = null;
      this.loadingEvents = true;

      try {
        const response = await getEvents(this.complaint._id);
        this.events = response.data;

        // Verificar automaticamente o status de eventos de curso que não estão concluídos
        for (const event of this.events) {
          if (
            event.type === 18 &&
            event.courseAssignment &&
            event.courseAssignment.status !== "completed"
          ) {
            try {
              await this.verifyCourseStatus(event, false);
            } catch (error) {
              // Silenciosamente continua se houver erro na verificação
              console.error("Erro ao verificar status do curso:", error);
            }
          }
        }
      } catch (error) {
        console.error("Erro ao carregar movimentações:", error);
      } finally {
        this.loadingEvents = false;
      }
    },
    async loadPreviousProtocolInfo() {
      if (this.complaint.previousProtocol) {
        try {
          const { data } = await findComplaintIdByProtocol(
            this.complaint.previousProtocol
          );
          this.previousProtocolId = data._id;
          this.previousProtocolType = data.type || "complaint";
        } catch {
          // eslint-disable no-empty
        }
      }
    },
    async loadMonitoring() {
      this.loadingMonitorings = true;
      const response = await getMonitoring(this.complaint._id);
      this.monitorings = response.data;
      this.loadingMonitorings = false;
    },
    async loadDenounceds() {
      this.loadingDenounceds = true;
      const response = await getDenounceds(this.complaint._id);
      this.denounceds = response.data;
      this.loadingDenounceds = false;
    },
    async onDenouncedRefresh() {
      await this.loadDenounceds();
      // Recarrega eventos para exibir imediatamente o evento de desvinculação
      await this.loadEvents();
    },
    downloadFile(file) {
      const link = `${window.location.origin}/api/complaints/${this.complaint._id}/files/${file._id}`;
      const a = document.createElement("a");
      a.setAttribute("download", "");
      a.setAttribute("href", link);
      a.setAttribute("target", "_blank");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    async onCommitteeChanged(updatedComplaint) {
      try {
        console.log("onCommitteeChanged recebeu:", updatedComplaint);
        // O modal agora passa a denúncia completa atualizada da API
        if (updatedComplaint && updatedComplaint.status !== undefined) {
          console.log(
            "Atualizando denúncia com status:",
            updatedComplaint.status
          );

          // Preservar o objeto company atual para evitar perda de dados (visto que a API retorna apenas o ID)
          const company = this.complaint.company;

          // Atualizar o objeto complaint com os dados retornados da API
          Object.assign(this.complaint, updatedComplaint);

          // Se a API retornou apenas o ID da empresa, restaurar o objeto completo
          if (
            updatedComplaint.company &&
            typeof updatedComplaint.company !== "object"
          ) {
            this.$set(this.complaint, "company", company);
          }

          // Garantir reatividade para propriedades específicas
          this.$set(this.complaint, "status", updatedComplaint.status);
          this.$set(this.complaint, "committee", updatedComplaint.committee);
        } else {
          console.log("Fallback: apenas atualizando comitê");
          // Fallback: se não recebeu a denúncia completa, apenas atualizar o comitê
          this.$set(this.complaint, "committee", updatedComplaint);
        }
        this.loadEvents();
      } catch (error) {
        console.error("Erro ao atualizar comitê:", error);
        // Em caso de erro, pelo menos atualizar o comitê localmente
        this.$set(this.complaint, "committee", updatedComplaint);
        this.loadEvents();
      }
    },
    openCommitteeModal() {
      this.$refs.committeeModal.show();
    },
    onClassificationChanged(classification) {
      this.$set(this.complaint, "classification", classification);
      this.loadEvents();
    },
    onStatusChanged(status) {
      this.$set(this.complaint, "status", status);
    },
    replacePhoneServiceProtocol() {
      this.phoneServiceProtocol = this.complaint.phoneServiceProtocol;
      this.oldPhoneServiceProtocol = this.complaint.phoneServiceProtocol;
      const date = moment(this.complaint.phoneServiceReceiveDate);
      this.phoneServiceReceiveDate = this.oldPhoneServiceReceiveDate =
        date.format("YYYY-MM-DD");
      this.phoneServiceReceiveTime = this.oldPhoneServiceReceiveTime =
        date.format("HH:mm");
      this.$set(this.complaint, "phoneServiceProtocol", null);
      this.$set(this.complaint, "phoneServiceReceiveDate", null);
    },
    cancelPhoneServiceProtocolReplace() {
      this.$set(
        this.complaint,
        "phoneServiceProtocol",
        this.oldPhoneServiceProtocol
      );
      let date = this.oldPhoneServiceReceiveDate;
      if (this.oldPhoneServiceReceiveTime) {
        date = moment(
          `${this.oldPhoneServiceReceiveDate} ${this.oldPhoneServiceReceiveTime}`
        ).format();
      }
      this.$set(this.complaint, "phoneServiceReceiveDate", date);
      this.oldPhoneServiceProtocol = null;
      this.oldPhoneServiceReceiveDate = null;
      this.oldPhoneServiceReceiveTime = null;
      this.phoneServiceProtocol = null;
    },
    async onAssociatePhoneServiceProtocol() {
      if (this.phoneServiceProtocol) {
        try {
          let date = this.phoneServiceReceiveDate;
          if (this.phoneServiceReceiveTime) {
            date = moment(
              `${this.phoneServiceReceiveDate} ${this.phoneServiceReceiveTime}`
            ).format();
          }
          await associatePhoneServiceProtocol(
            this.complaint._id,
            this.phoneServiceProtocol,
            date
          );
          this.$set(
            this.complaint,
            "phoneServiceProtocol",
            this.phoneServiceProtocol
          );
          this.$set(this.complaint, "phoneServiceReceiveDate", date);
          this.$bvToast.toast("Protocolo telefônico associado com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        } catch (error) {
          if (error.response.status === 403 || error.response.status === 404) {
            this.$bvToast.toast(error.response.data, {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            });
          } else {
            this.$bvToast.toast(error.response.statusText, {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            });
          }
        }
      }
    },
    openIrrelevantModal(event) {
      this.irrelevantModal.event = event;
      this.irrelevantModal.justification = "";
      this.irrelevantModal.errorMessage = "";
      this.irrelevantModal.submitting = false;
      this.$refs.irrelevantModalRef.show();
    },

    onHideIrrelevantModal() {
      if (this.irrelevantModal.submitting) return;
      this.irrelevantModal.event = null;
      this.irrelevantModal.justification = "";
      this.irrelevantModal.errorMessage = "";
    },

    async confirmIrrelevant(bvEvt) {
      const event = this.irrelevantModal.event;
      if (!event) return;

      bvEvt.preventDefault();

      if (!this.irrelevantModal.justification.trim()) {
        this.irrelevantModal.errorMessage = "Justificativa é obrigatória";
        return;
      }

      try {
        this.irrelevantModal.submitting = true;
        await setEventIrrelevant(
          this.complaint._id,
          event._id,
          true,
          this.irrelevantModal.justification.trim()
        );
        this.$bvToast.toast("Movimentação marcada como irrelevante.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        await this.loadEvents();
        this.$refs.irrelevantModalRef.hide();
      } catch (e) {
        this.irrelevantModal.errorMessage =
          (e && e.response && e.response.data) ||
          (e && e.message) ||
          "Erro ao atualizar movimentação";
      } finally {
        this.irrelevantModal.submitting = false;
      }
    },

    async toggleMonitoringVisibility(monitoring) {
      try {
        await setMonitoringVisibility(monitoring, !monitoring.hidden);
        this.$set(monitoring, "hidden", !monitoring.hidden);
        this.$bvToast.toast(
          `Acompanhamento ${
            monitoring.hidden ? "ocultado" : "exibido"
          } com sucesso!`,
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
      } catch (error) {
        if (error.response.status === 403 || error.response.status === 404) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },
    async toggleMonitoringResponse(complaint) {
      try {
        await setMonitoringResponseEnabled(
          complaint,
          !complaint.monitoringResponseEnabled
        );
        this.$set(
          complaint,
          "monitoringResponseEnabled",
          !complaint.monitoringResponseEnabled
        );
        this.$bvToast.toast(
          `Resposta ${
            complaint.monitoringResponseEnabled ? "habilitada" : "desabilitada"
          } com sucesso!`,
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
        await this.loadEvents();
      } catch (error) {
        if (error.response.status === 403 || error.response.status === 404) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },
    async loadClassifications() {
      this.$store.dispatch(
        "RefreshCompanyClassifications",
        this.complaint.company._id
      );
    },
    eventCommitteeAddedList(committee) {
      const result = committee.new.users
        .filter((user) => !committee.old.users.includes(user))
        .map(
          (user) =>
            `${this.getCommitteeUser(user).name} ${
              this.getCommitteeUser(user).surname
            } (comitê)`
        );
      if (committee.new.admin && committee.new.admin !== committee.old.admin) {
        const user = committee.new.admin;
        result.push(
          `${this.getCommitteeUser(user).name} ${
            this.getCommitteeUser(user).surname
          } (relator)`
        );
      }
      return result.join(", ");
    },
    eventCommitteeRemovedList(committee) {
      const result = committee.old.users
        .filter((user) => !committee.new.users.includes(user))
        .map(
          (user) =>
            `${this.getCommitteeUser(user).name} ${
              this.getCommitteeUser(user).surname
            } (comitê)`
        );
      if (committee.old.admin && committee.old.admin !== committee.new.admin) {
        const user = committee.old.admin;
        result.push(
          `${this.getCommitteeUser(user).name} ${
            this.getCommitteeUser(user).surname
          } (relator)`
        );
      }
      return result.join(", ");
    },
    eventCommitteeCurrent(committee) {
      const result = committee.new.users.map(
        (user) =>
          `${this.getCommitteeUser(user).name} ${
            this.getCommitteeUser(user).surname
          } (comitê)`
      );
      if (committee.new.admin) {
        const user = committee.new.admin;
        result.push(
          `${this.getCommitteeUser(user).name} ${
            this.getCommitteeUser(user).surname
          } (relator)`
        );
      }
      if (result.length === 0) {
        return "Sem comissão";
      }
      return result.join(", ");
    },
    eventClassificationChangedList(data) {
      const result = Object.entries(data.new)
        .filter(([key, value]) => data.old[key] !== value)
        .map(([key, value]) => {
          return {
            name: ClassificationName[key],
            new: this.classificationMaps[key][value],
            old: this.classificationMaps[key][data.old[key]],
          };
        });
      return result;
    },
  },
};
</script>

<style lang="scss">
@import "./styles.scss";
</style>
